import React, { useState, useMemo, useEffect } from 'react';
import { Button } from './button';
import { Input } from './input';
import {
    Loader2, Check, X, AlertTriangle, ChevronRight, MapPin, Search, Ruler, Package,
} from 'lucide-react';
import {
    FORMATS,
    PRINT_MIN_DPI,
} from '../config/prices';
import { Dialog, DialogContent, DialogTitle } from './dialog';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// ---------------------------------------------------------------------------
// Catalogue + quote API
// ---------------------------------------------------------------------------

// Tiny module-level cache so the catalogue isn't re-fetched on every modal
// open. The catalogue rarely changes; we revalidate whenever the modal
// remounts (page load).
let _catalogueCache = null;

// Per-image selection cache. Keyed by the image URL (the only stable
// identifier MapPrintSection gets from its parent) so reopening the
// same image preserves the user's product / size / colour / address
// / voucher state without leaking selections across images.
const _selectionCache = new Map();

async function fetchCatalogue() {
    if (_catalogueCache) return _catalogueCache;
    const r = await fetch(`${API_URL}/prodigi/catalogue`);
    if (!r.ok) throw new Error('Failed to load catalogue');
    const data = await r.json();
    _catalogueCache = data.products || [];
    return _catalogueCache;
}

async function fetchQuote({ productId, tier, country = 'DE', color = null }) {
    const params = new URLSearchParams({ product_id: productId, tier, country });
    if (color) params.set('color', color);
    const r = await fetch(`${API_URL}/prodigi/quote?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (!r.ok) {
        // Pull the upstream Prodigi error body through to the console
        // so the dev tools network tab shows something actionable
        // (instead of a bare "Quote failed").
        let detail = '';
        try {
            const body = await r.json();
            detail = body.prodigi_body || body.details || body.error || '';
            console.error('[mapprints] quote failed', body);
        } catch (_) { /* not JSON */ }
        throw new Error(`Quote failed${detail ? ': ' + detail : ''}`);
    }
    return r.json();
}

// ---------------------------------------------------------------------------
// Visual helpers
// ---------------------------------------------------------------------------

// Frame colour swatches — used both in the colour picker and to tint the
// preview frame border. Same palette across all materials.
const COLOR_HEX = {
    black: '#1a1a1a',
    white: '#fafafa',
    natural: '#c08a52',
    silver: '#c0c4c8',
    oak: '#8b5a2b',
};

function getFrameStyle(product, colorId) {
    // Paper-only products → no frame in the preview.
    if (!product || product.kind !== 'framed') {
        return { background: 'transparent', boxShadow: 'none' };
    }
    const base = COLOR_HEX[colorId] || '#888';
    // Mix a small triple-stop gradient so the frame has a hint of depth.
    return {
        background: `linear-gradient(135deg, ${base} 0%, ${shade(base, 18)} 50%, ${shade(base, -22)} 100%)`,
        boxShadow: 'inset 0 0 6px rgba(0,0,0,0.4)',
    };
}

function shade(hex, percent) {
    const num = parseInt(hex.slice(1), 16);
    const r = Math.max(0, Math.min(255, (num >> 16) + percent));
    const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + percent));
    const b = Math.max(0, Math.min(255, (num & 0xff) + percent));
    return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

const FRAME_BORDER_PX = 22;

function FramedPreview({ imageUrl, product, colorId, isPortrait, sideBySide, compact }) {
    const style = getFrameStyle(product, colorId);
    const borderPx = product?.kind === 'framed' ? FRAME_BORDER_PX : 0;
    const noChrome = !product || product.kind !== 'framed';

    // `compact` is set when the summary view is showing — on mobile,
    // the bottom strip has more content (cost breakdown, voucher row,
    // shipping picker, Place Order), so we drop the image's mobile
    // cap to leave room. Desktop is unchanged because there's already
    // plenty of vertical space in the side-by-side layout.
    let imgMaxH;
    if (compact) {
        imgMaxH = sideBySide
            ? 'max-h-[20vh] lg:max-h-[52vh]'
            : 'max-h-[18vh] sm:max-h-[40vh]';
    } else {
        imgMaxH = sideBySide
            ? 'max-h-[32vh] lg:max-h-[52vh]'
            : 'max-h-[30vh] sm:max-h-[40vh]';
    }

    return (
        <div className="flex justify-center">
            <div
                style={{
                    ...style,
                    padding: borderPx,
                    maxWidth: '100%',
                    filter: 'drop-shadow(0 12px 22px rgba(0,0,0,0.35))',
                }}
            >
                <div
                    className={noChrome ? '' : 'bg-white p-2'}
                >
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt="Map preview"
                            className={`block max-w-full ${imgMaxH} w-auto h-auto select-none`}
                            draggable={false}
                        />
                    ) : (
                        <div className="aspect-[4/3] w-full bg-gray-200 flex items-center justify-center text-gray-400 text-xs">
                            No preview
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function ColorSwatch({ colorId, selected, onClick, title }) {
    const fill = COLOR_HEX[colorId] || '#888';
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            className={`relative h-7 w-7 rounded-full border-2 transition-all ${selected ? 'border-blue-500 scale-110' : 'border-gray-300 hover:border-gray-500'
                }`}
            style={{ background: fill }}
        />
    );
}

// ---------------------------------------------------------------------------
// Shipping dialog (unchanged from the pre-Prodigi version)
// ---------------------------------------------------------------------------

const SHIPPING_COUNTRIES = [
    { code: 'DE', name: 'Germany' },
    { code: 'AT', name: 'Austria' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'BE', name: 'Belgium' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'PT', name: 'Portugal' },
    { code: 'DK', name: 'Denmark' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'FI', name: 'Finland' },
    { code: 'PL', name: 'Poland' },
    { code: 'CZ', name: 'Czechia' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'HU', name: 'Hungary' },
    { code: 'IE', name: 'Ireland' },
    { code: 'GB', name: 'United Kingdom' },
];
const SHIPPING_COUNTRY_CODES = new Set(SHIPPING_COUNTRIES.map(c => c.code));

function isShippingComplete(s) {
    if (!s) return false;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((s.email || '').trim())) return false;
    return ['name', 'line1', 'postal_code', 'city', 'country']
        .every(f => (s[f] || '').toString().trim().length > 0);
}

function AddressField({ label, value, onChange, type = 'text', placeholder, required, autoComplete, className = '' }) {
    return (
        <label className={`block ${className}`}>
            <span className="block text-xs font-medium text-gray-600 mb-1">
                {label}{required && <span className="text-red-500">*</span>}
            </span>
            <input
                type={type}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                autoComplete={autoComplete}
                className="w-full rounded-xl px-3 py-2 text-sm sm:text-base border border-gray-400 bg-white
                    focus:outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10
                    placeholder:text-gray-400 transition-colors"
            />
        </label>
    );
}

function nominatimToShipping(item) {
    const a = item.address || {};
    const houseNo = a.house_number || '';
    const road = a.road || a.pedestrian || a.path || '';
    const line1 = [road, houseNo].filter(Boolean).join(' ').trim();
    const city = a.city || a.town || a.village || a.municipality || a.hamlet || '';
    const postal = a.postcode || '';
    const countryCode = (a.country_code || '').toUpperCase();
    return {
        line1,
        postal_code: postal,
        city,
        country: SHIPPING_COUNTRY_CODES.has(countryCode) ? countryCode : null,
    };
}

// Nominatim's `display_name` is a long comma-separated dump
// ("30, Schmellerstraße, Am Schlachthof, München, Bayern, …"). For the
// dropdown we re-assemble the same fields into the local-postal-format
// the user is actually about to fill: "Schmellerstraße 30, 80337 München".
function formatNominatimDisplay(item) {
    const a = item.address || {};
    const houseNo = a.house_number || '';
    const road = a.road || a.pedestrian || a.path || a.amenity || '';
    const streetLine = [road, houseNo].filter(Boolean).join(' ').trim();
    const postal = a.postcode || '';
    const city = a.city || a.town || a.village || a.municipality || a.hamlet || '';
    const cityLine = [postal, city].filter(Boolean).join(' ').trim();
    const parts = [streetLine, cityLine].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : item.display_name;
}

function AddressLookup({ onPick }) {
    const [q, setQ] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    // After the user clicks a result we rewrite `q` to the pretty form,
    // which would normally re-trigger the debounced lookup below and
    // re-open the dropdown ~400ms later. This ref lets the pick handler
    // skip exactly one search cycle without freezing future searches.
    const skipNextLookup = React.useRef(false);

    useEffect(() => {
        if (skipNextLookup.current) {
            skipNextLookup.current = false;
            return;
        }
        const text = q.trim();
        if (text.length < 3) { setResults([]); return; }
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(text)}&format=json&addressdetails=1&limit=5`;
                const r = await fetch(url, { headers: { 'Accept-Language': 'en' } });
                if (!r.ok) throw new Error('lookup failed');
                const data = await r.json();
                setResults(Array.isArray(data) ? data : []);
                setOpen(true);
            } catch (_e) {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 400);
        return () => clearTimeout(t);
    }, [q]);

    return (
        <div className="relative">
            <span className="block text-xs font-medium text-gray-600 mb-1">
                Search address
            </span>
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onFocus={() => results.length > 0 && setOpen(true)}
                    placeholder="e.g. Schmellerstraße 30, München"
                    className="w-full rounded-xl pl-9 pr-9 py-2 text-sm sm:text-base border border-gray-500 bg-white
                        focus:outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10
                        placeholder:text-gray-400 transition-colors"
                />
                {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />}
                {!loading && q && (
                    <button type="button"
                        onClick={() => { setQ(''); setResults([]); setOpen(false); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                )}
            </div>
            {open && results.length > 0 && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {results.map(r => {
                        const pretty = formatNominatimDisplay(r);
                        return (
                            <button key={r.place_id} type="button"
                                onClick={() => {
                                    const filled = nominatimToShipping(r);
                                    onPick(filled);
                                    skipNextLookup.current = true;
                                    setQ(pretty);
                                    setResults([]);
                                    setOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                            >
                                {pretty}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function ShippingAddressDialog({ open, onClose, value, onChange }) {
    const [draft, setDraft] = useState(value);
    useEffect(() => { if (open) setDraft(value); }, [open, value]);
    const valid = isShippingComplete(draft);
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md p-5 sm:p-7 !rounded-2xl sm:!rounded-2xl">
                <DialogTitle className="text-lg font-semibold mb-3">Shipping address</DialogTitle>
                {/* Single tight column. Postal code + City sit on one row
                    at every viewport so the mobile flow doesn't need
                    9 separate rows of scroll. The px-0.5 keeps the
                    input focus rings from kissing the dialog edge. */}
                <div className="space-y-2.5 max-h-[70vh] overflow-y-auto px-0.5 pl-3 pr-3">
                    <AddressLookup
                        onPick={(picked) => setDraft(s => ({
                            ...s,
                            line1: picked.line1 || s.line1,
                            postal_code: picked.postal_code || s.postal_code,
                            city: picked.city || s.city,
                            country: picked.country || s.country,
                        }))}
                    />
                    <AddressField label="Full name" required value={draft.name}
                        onChange={(e) => setDraft(s => ({ ...s, name: e.target.value }))}
                        autoComplete="name" />
                    <AddressField label="Email" type="email" required value={draft.email}
                        onChange={(e) => setDraft(s => ({ ...s, email: e.target.value }))}
                        autoComplete="email" />
                    <AddressField label="Phone" type="tel" value={draft.phone}
                        onChange={(e) => setDraft(s => ({ ...s, phone: e.target.value }))}
                        autoComplete="tel" placeholder="optional" />
                    <AddressField label="Street + number" required value={draft.line1}
                        onChange={(e) => setDraft(s => ({ ...s, line1: e.target.value }))}
                        autoComplete="address-line1" />
                    <AddressField label="Address line 2" value={draft.line2}
                        onChange={(e) => setDraft(s => ({ ...s, line2: e.target.value }))}
                        autoComplete="address-line2" placeholder="optional" />
                    <div className="grid grid-cols-3 gap-2">
                        <AddressField label="Postal code" required value={draft.postal_code}
                            onChange={(e) => setDraft(s => ({ ...s, postal_code: e.target.value }))}
                            autoComplete="postal-code" />
                        <AddressField label="City" required value={draft.city}
                            onChange={(e) => setDraft(s => ({ ...s, city: e.target.value }))}
                            autoComplete="address-level2" className="col-span-2" />
                    </div>
                    <label className="block">
                        <span className="block text-xs font-medium text-gray-600 mb-1">Country<span className="text-red-500">*</span></span>
                        <select value={draft.country}
                            onChange={(e) => setDraft(s => ({ ...s, country: e.target.value }))}
                            autoComplete="country"
                            className="w-full px-3 py-2 pr-9 text-sm sm:text-base border border-gray-500 rounded-xl bg-white appearance-none cursor-pointer
                                focus:outline-none focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 transition-colors">
                            {SHIPPING_COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
                        </select>
                    </label>
                </div>
                <div className="flex items-center justify-between pt-3 gap-2">
                    <span className="text-[11px] text-gray-500">{valid ? 'Ready to go.' : 'Required fields marked with *'}</span>
                    <Button variant="black" size="sm" onClick={() => { onChange(draft); onClose(); }} disabled={!valid}>
                        <Check className="h-4 w-4 mr-1" /> Save
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Product / size / colour pickers
// ---------------------------------------------------------------------------

function PickerButton({ icon: Icon, title, subtitle, complete, onClick, priceLabel, incompleteVariant = 'default' }) {
    // `incompleteVariant="danger"` switches the unfilled state from
    // neutral grey to a red callout — used on the shipping address
    // picker where leaving it blank blocks the order.
    const danger = !complete && incompleteVariant === 'danger';
    return (
        <button type="button" onClick={onClick}
            className={`w-full flex items-center gap-2 px-3 py-2.5 border rounded-xl transition-colors text-left ${complete
                ? 'bg-emerald-50 border-emerald-300 hover:bg-emerald-100'
                : danger
                    ? 'bg-red-50 border-red-300 hover:bg-red-100'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
        >
            {Icon && <Icon className={`h-4 w-4 flex-shrink-0 ${complete ? 'text-emerald-600' : danger ? 'text-red-600' : 'text-gray-500'}`} />}
            <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium leading-tight ${danger ? 'text-red-700' : ''}`}>{title}</div>
                {subtitle && <div className={`text-[11px] truncate ${danger ? 'text-red-600' : 'text-gray-600'}`}>{subtitle}</div>}
            </div>
            {/* Price label appears immediately left of the trailing badge
                so it sits adjacent to (and the same height as) the
                green checkmark for visual symmetry across all picker
                rows (Product / Size / Frame colour). */}
            {priceLabel && (
                <span className="flex-shrink-0 text-sm font-semibold text-gray-900">
                    {priceLabel}
                </span>
            )}
            {complete ? (
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-emerald-500 flex items-center justify-center">
                    <Check className="h-4 w-4 text-white" />
                </div>
            ) : (
                <ChevronRight className={`h-4 w-4 flex-shrink-0 ${danger ? 'text-red-500' : 'text-gray-400'}`} />
            )}
        </button>
    );
}

function ProductDialog({ open, onClose, productId, onChange, catalogue, minPrices }) {
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md p-5 !rounded-2xl sm:!rounded-2xl">
                <DialogTitle className="text-base font-semibold mb-1">Choose a product</DialogTitle>
                <p className="text-sm text-gray-500 mb-3">Printed and shipped by our print partner. Prices shown are for the smallest size; larger sizes show in the next step.</p>
                <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1.5 -mr-1.5">
                    {catalogue.map(p => {
                        const selected = p.id === productId;
                        const fromPrice = minPrices && minPrices[p.id];
                        return (
                            <button key={p.id} type="button"
                                onClick={() => { onChange(p.id); onClose(); }}
                                className={`w-full flex items-start gap-3 p-3 border rounded-2xl text-left transition-colors pr-3 ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                                    }`}>
                                <div className="w-12 h-9 rounded-sm flex-shrink-0"
                                    style={getFrameStyle(p, (p.colors || [])[0]?.id || 'black')}>
                                    <div className="w-full h-full bg-white/80 m-1"
                                        style={{ width: 'calc(100% - 8px)', height: 'calc(100% - 8px)' }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm">{p.label}</div>
                                    <div className="text-xs text-gray-500">{p.subtitle}</div>
                                </div>
                                <div className="flex-shrink-0 text-right">
                                    <div className="text-xs text-gray-500">from</div>
                                    <div className="text-sm font-semibold text-gray-900">
                                        {fromPrice != null
                                            ? `€${fromPrice.toFixed(2)}`
                                            : <Loader2 className="h-3.5 w-3.5 inline animate-spin text-gray-400" />}
                                    </div>
                                </div>
                                {/* No trailing checkmark — the
                                    border + light blue background
                                    already mark the selection. */}
                            </button>
                        );
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function SizeDialog({ open, onClose, product, tier, onChange, availableTiers, quotes }) {
    if (!product) return null;
    // Cheapest tier sets the "+€0.00" baseline; everything else shows
    // the upcharge so the product card's "from €X" + the size's "+€Y"
    // add up to the price the user actually pays.
    const tierPrices = Object.values(quotes || {})
        .map(q => q?.item_price)
        .filter(v => typeof v === 'number');
    const cheapest = tierPrices.length > 0 ? Math.min(...tierPrices) : 0;
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md p-5 !rounded-2xl sm:!rounded-2xl">
                <DialogTitle className="text-base font-semibold mb-3">Choose a size</DialogTitle>
                <div className="grid grid-cols-2 gap-2.5 pr-1.5 -mr-1.5">
                    {product.sizes.map(s => {
                        const isSelected = tier === s.tier;
                        const isDisabled = availableTiers && !availableTiers[s.tier];
                        const quote = quotes && quotes[s.tier];
                        const delta = quote ? quote.item_price - cheapest : null;
                        return (
                            <button key={s.tier} disabled={isDisabled}
                                onClick={() => { onChange(s.tier); onClose(); }}
                                className={`p-3 border rounded-xl text-left transition-colors shadow-sm
                                    ${isSelected
                                        ? 'bg-gray-900 text-white border-gray-900 shadow-md'
                                        : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'}
                                    ${isDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                            >
                                <div className="font-semibold text-base">{s.tier}</div>
                                <div className={`text-xs ${isSelected ? 'text-gray-300' : 'text-gray-500'}`}>{s.cm} cm</div>
                                <div className={`text-sm font-medium mt-1 ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                                    {delta == null ? '…' : `+€${delta.toFixed(2)}`}
                                </div>
                            </button>
                        );
                    })}
                </div>
                {/* Resolution warning lives here (not in the main panel)
                    because it's specifically about the size grid above. */}
                {availableTiers && Object.values(availableTiers).some(v => !v) && (
                    <div className="mt-3 flex items-center justify-center gap-2 px-3 py-2 bg-yellow-100 border border-yellow-300 rounded-lg">
                        <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-600 text-center">
                            Some sizes are unavailable — the rendered map doesn't have enough resolution.
                        </p>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function ColorDialog({ open, onClose, colors, value, onChange }) {
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-xs p-5 !rounded-2xl sm:!rounded-2xl">
                <DialogTitle className="text-base font-semibold mb-3">Frame colour</DialogTitle>
                <div className="space-y-2">
                    {(colors || []).map(c => (
                        <button key={c.id} type="button"
                            onClick={() => { onChange(c.id); onClose(); }}
                            className={`w-full flex items-center gap-3 p-3 border rounded-xl text-left transition-colors ${value === c.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                                }`}>
                            <ColorSwatch colorId={c.id} selected={value === c.id} onClick={() => { }} title={c.label} />
                            <span className="text-sm font-medium flex-1">{c.label}</span>
                            {value === c.id && <Check className="h-4 w-4 text-blue-600" />}
                        </button>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Voucher row (reused inside the summary dialog)
// ---------------------------------------------------------------------------

function VoucherRow({ voucher, subtotalCents, rejected }) {
    if (!voucher) return null;
    const {
        code, setCode, discount, discountType, isApplying, errorMsg, setErrorMsg,
        buttonState, apply, clear, getApplyButtonStyles,
    } = voucher;

    // Does this voucher have leftover value beyond the order total?
    // If so, the user gets the rest credited back as a new voucher
    // (the backend's _redeem_voucher mints it). We surface that as a
    // tiny grey hint right next to the "X voucher applied" line —
    // no box, no separate row.
    let remainderHint = null;
    if (!rejected && discount > 0 && discountType === 'amount' && subtotalCents != null) {
        const remainder = discount - Math.min(subtotalCents, discount);
        if (remainder > 0) {
            remainderHint = (
                <span className="text-xs text-gray-500 ml-2">
                    (rest is credited as new voucher)
                </span>
            );
        }
    }

    return (
        <div>
            {/* Status line above the input. One of three states:
                  - rejected   → red "This voucher is not allowed for prints."
                  - applied    → green "€X voucher applied" + optional remainder hint
                  - none/empty → grey "Have a voucher? Apply it here."
                All three render in the same row so they don't shift
                layout when swapping. */}
            <div className="flex flex-col items-center">
                {rejected ? (
                    <div className="text-red-600 font-normal mb-1 text-center text-sm">
                        This voucher is not allowed for prints.
                    </div>
                ) : discount ? (
                    <div className="text-green-600 font-normal mb-1 text-center text-sm">
                        {discountType === 'percent'
                            ? `${discount}% discount applied`
                            : `€${(discount / 100).toFixed(2)} voucher applied`}
                        {remainderHint}
                    </div>
                ) : (
                    <div className="text-sm text-gray-500 mb-1">Have a voucher? Apply it here.</div>
                )}
            </div>
            <div className="flex justify-center items-center w-full">
                <div className="flex gap-2 items-center w-full max-w-xs">
                    <div className="relative flex-1">
                        <Input type="text" placeholder="Voucher code" value={code}
                            onChange={(e) => {
                                setCode(e.target.value);
                                if (errorMsg) setErrorMsg(null);
                                // If a voucher's already been applied (which
                                // would normally disable the apply button), drop
                                // it as soon as the user starts editing — they're
                                // clearly trying a different code. Without this
                                // the apply button stays disabled after a
                                // rejected voucher until they hit the × button.
                                if (discount) clear();
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter' && code && !isApplying && !discount) apply(); }}
                            className={`w-full bg-gray-300/30 rounded-xl text-center focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-gray-400 text-sm transition-colors ${errorMsg ? 'border border-red-500 placeholder:text-red-400' : 'border-0'
                                }`} />
                        {code && (
                            <button onClick={clear} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                    <Button onClick={apply} disabled={!code || isApplying || discount}
                        size="icon" variant="black"
                        className={`relative h-9 w-9 transition-colors ${getApplyButtonStyles ? getApplyButtonStyles() : ''}`}>
                        {isApplying ? <Loader2 className="h-4 w-4 animate-spin" />
                            : buttonState === 'error' ? <X className="h-4 w-4" />
                                : <Check className="h-4 w-4" />}
                    </Button>
                </div>
            </div>
            {errorMsg && <p className="mt-1 text-xs text-red-500 text-center" role="alert">{errorMsg}</p>}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Summary dialog (final review + voucher + Place Order)
// ---------------------------------------------------------------------------

// Inline summary view. Rendered in the same slot as `choicesBlock`
// (right column for portrait, bottom strip for landscape) when the
// user clicks "Continue to summary" — no popup. The image stays in
// place so the customer keeps eyes on what they're buying.
function SummaryPanel({
    onBack, onConfirm, isUnlocking,
    product, color, quote, voucher, applyVoucher,
    isFree, shippingComplete, freeShipping,
    onOpenShippingDialog,
    tier,
    voucherRejected,
}) {
    if (!product) return null;
    const itemPrice = quote ? quote.item_price : 0;
    const shipping = quote ? quote.shipping_price : 0;
    const grossTotal = itemPrice + shipping;
    const discounted = applyVoucher ? applyVoucher(grossTotal) : grossTotal;
    const discountAmount = grossTotal - discounted;
    const total = isFree ? 0 : discounted;

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center justify-between mb-3">
                <button
                    type="button"
                    onClick={onBack}
                    className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                >
                    <ChevronRight className="h-4 w-4 rotate-180" />
                    Back
                </button>
                <h3 className="text-base font-semibold">Order summary</h3>
                <span className="w-12" />
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto -mr-1 pr-1">
                <div className="text-sm space-y-0.5">
                    <div className="font-semibold">
                        {product.label}
                        <span className="text-gray-500 font-normal"> · Size {tier}</span>
                    </div>
                    {color && product.kind === 'framed' && (
                        <div className="text-gray-700">Colour: {color}</div>
                    )}
                </div>

                <div className="border-t border-gray-200 pt-3">
                    <VoucherRow
                        voucher={voucher}
                        subtotalCents={Math.round((grossTotal || 0) * 100)}
                        rejected={voucherRejected}
                    />
                    {/* The rejection message is now rendered inside
                        VoucherRow as red text in place of the
                        "X voucher applied" line, so no separate
                        warning box is needed here any more. */}
                </div>

                {isFree && (
                    <div className="border-t border-gray-200 pt-3">
                        <div className="text-xs font-medium text-gray-700 mb-1.5">Shipping address</div>
                        <PickerButton
                            icon={MapPin}
                            title={shippingComplete ? freeShipping.name : 'Enter shipping address'}
                            subtitle={
                                shippingComplete
                                    ? `${freeShipping.line1}, ${freeShipping.postal_code} ${freeShipping.city}, ${freeShipping.country}`
                                    : 'Name, email, address, country'
                            }
                            complete={shippingComplete}
                            incompleteVariant="danger"
                            onClick={onOpenShippingDialog}
                        />
                    </div>
                )}

                <div className="border-t border-gray-200 pt-3 space-y-1 text-sm">
                    <div className="flex justify-between text-gray-700">
                        <span>{product.label}</span>
                        <span>€{itemPrice.toFixed(2)}</span>
                    </div>
                    {/* Shipping line always shows the real cost so the
                        voucher math is transparent — if the voucher
                        covers everything the discount row right below
                        zeroes it out. */}
                    <div className="flex justify-between text-gray-700">
                        <span>Shipping</span>
                        <span>€{shipping.toFixed(2)}</span>
                    </div>
                    {discountAmount > 0 && (
                        <div className="flex justify-between text-green-600">
                            <span>Voucher discount</span>
                            <span>−€{discountAmount.toFixed(2)}</span>
                        </div>
                    )}
                    <div className="flex justify-between items-baseline pt-2 border-t border-gray-100">
                        <span className="font-semibold">Total</span>
                        {discountAmount > 0 ? (
                            <span>
                                <span className="line-through text-gray-400 mr-2 text-sm font-normal">
                                    €{grossTotal.toFixed(2)}
                                </span>
                                <span className="text-lg font-semibold text-green-600">
                                    €{total.toFixed(2)}
                                </span>
                            </span>
                        ) : (
                            <span className="text-lg font-semibold">€{total.toFixed(2)}</span>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex flex-col items-center mt-3">
                <Button variant="black" onClick={onConfirm}
                    disabled={isUnlocking || (isFree && !shippingComplete) || !quote}
                    className="w-full text-sm sm:text-base">
                    {isUnlocking ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processing...</>
                    ) : (
                        <><Check className="mr-2 h-4 w-4" />{isFree ? 'Place Order' : 'Continue to payment'}</>
                    )}
                </Button>
                <p className="text-sm text-gray-500 text-center mt-2 px-2">
                    {isFree
                        ? (shippingComplete
                            ? 'Free order — no shipping charge. EU delivery within 7–10 business days.'
                            : 'Add a shipping address above to place the order.')
                        : "You'll confirm shipping address and pay on the next step."}
                </p>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const MapPrintSection = ({
    finalImage,
    maxDpis,
    selectedFormat,
    onPrintSelect,
    isUnlocking,
    isPortrait,
    voucher,
}) => {
    const [catalogue, setCatalogue] = useState([]);
    const [catalogueErr, setCatalogueErr] = useState(null);

    // Lazy initial-state from the per-image cache so reopening the
    // modal for the same image picks up where the user left off.
    const cacheKey = finalImage || null;
    const cached = cacheKey ? _selectionCache.get(cacheKey) : null;

    const [productId, setProductId] = useState(cached?.productId ?? null);
    const [tier, setTier] = useState(cached?.tier ?? 'S');
    const [color, setColor] = useState(cached?.color ?? null);

    const [freeShipping, setFreeShipping] = useState(cached?.freeShipping ?? {
        name: '', email: '', phone: '',
        line1: '', line2: '', postal_code: '', city: '', country: 'DE',
    });
    const [freeShippingError, setFreeShippingError] = useState(null);

    // Persist selections back into the cache as they change. Skip
    // until productId is set so we don't write a stub entry on first
    // mount before the catalogue resolves.
    useEffect(() => {
        if (!cacheKey || !productId) return;
        _selectionCache.set(cacheKey, { productId, tier, color, freeShipping });
    }, [cacheKey, productId, tier, color, freeShipping]);

    const [productDialogOpen, setProductDialogOpen] = useState(false);
    const [sizeDialogOpen, setSizeDialogOpen] = useState(false);
    const [colorDialogOpen, setColorDialogOpen] = useState(false);
    const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
    // The right side / bottom area swaps between the catalogue
    // chooser and the order summary instead of opening a separate
    // popup. Image stays put through the swap.
    const [view, setView] = useState('choose'); // 'choose' | 'summary'

    // Live quote keyed by tier — so the size dialog can show all four
    // prices at once. Refetched whenever product or country changes.
    const [quotes, setQuotes] = useState({});
    // True while the per-tier quotes for the selected product are in
    // flight; drives the "Calculating price…" button state.
    const [quotesLoading, setQuotesLoading] = useState(false);

    // "From €X" entry per product, so the product dialog can show a
    // price next to each option without the user having to click in.
    // Built from a single (cheapest-tier) quote per product when the
    // catalogue first loads — 7 calls in parallel, then cached for
    // the lifetime of the modal.
    const [minPrices, setMinPrices] = useState({});

    // 1) Load the catalogue once on mount.
    useEffect(() => {
        let cancelled = false;
        fetchCatalogue()
            .then(list => {
                if (cancelled) return;
                setCatalogue(list);
                if (!productId && list.length > 0) setProductId(list[0].id);
            })
            .catch(e => { if (!cancelled) setCatalogueErr(e.message); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const product = useMemo(() => catalogue.find(p => p.id === productId) || null, [catalogue, productId]);

    // 2) When product or country changes, refetch quotes for every tier
    // of that product so the SizeDialog can show prices live. Framed
    // products need a colour for the quote — wait until the colour
    // state has settled (default-colour effect runs in the next tick)
    // before firing so we don't burn quotes that would 400.
    useEffect(() => {
        if (!product) return;
        if (product.kind === 'framed' && !color) return;
        let cancelled = false;
        setQuotes({});
        setQuotesLoading(true);
        const country = freeShipping.country || 'DE';
        Promise.all(product.sizes.map(s =>
            fetchQuote({ productId: product.id, tier: s.tier, country, color })
                .then(q => ({ tier: s.tier, q }))
                .catch(() => ({ tier: s.tier, q: null }))
        )).then(results => {
            if (cancelled) return;
            const next = {};
            for (const { tier, q } of results) if (q) next[tier] = q;
            setQuotes(next);
            setQuotesLoading(false);
        });
        return () => { cancelled = true; };
    }, [product, freeShipping.country, color]);

    // 2b) Prefetch a single (cheapest-tier) quote per product so the
    // product-selection dialog can render "from €X" alongside each
    // option. Fires once when the catalogue first arrives — results
    // stay cached in `minPrices` for the modal's lifetime.
    useEffect(() => {
        if (!catalogue.length) return;
        let cancelled = false;
        const country = freeShipping.country || 'DE';
        Promise.all(catalogue.map(p => {
            const firstTier = (p.sizes[0] || {}).tier;
            if (!firstTier) return Promise.resolve({ id: p.id, q: null });
            // Framed products require a colour; use the first one as
            // a representative price (colour rarely changes Prodigi's
            // price, so the "from" tier is accurate either way).
            const c = p.kind === 'framed' ? (p.colors?.[0]?.id || 'black') : null;
            return fetchQuote({ productId: p.id, tier: firstTier, country, color: c })
                .then(q => ({ id: p.id, q }))
                .catch(() => ({ id: p.id, q: null }));
        })).then(results => {
            if (cancelled) return;
            const next = {};
            for (const { id, q } of results) if (q) next[id] = q.item_price;
            setMinPrices(next);
        });
        return () => { cancelled = true; };
    }, [catalogue, freeShipping.country]);

    // 3) Default colour when picking a framed product. Also snaps the
    //    current colour to the first allowed one when the cached pick
    //    is no longer in the product's `colors` list (catalogue can
    //    tighten allowed colours per SKU — e.g. budget_framed_poster
    //    only stocks white/natural — and we'd otherwise send a stale
    //    "black" to the backend, which rejects the checkout with 400).
    useEffect(() => {
        if (!product) return;
        if (product.kind === 'framed') {
            const allowedIds = (product.colors || []).map((c) => c.id);
            if (allowedIds.length > 0 && (!color || !allowedIds.includes(color))) {
                setColor(allowedIds[0]);
            }
        }
        if (product.kind !== 'framed' && color) setColor(null);
    }, [product, color]);

    // 4) When tier becomes unavailable for the new product, fall back.
    useEffect(() => {
        if (!product) return;
        if (!product.sizes.some(s => s.tier === tier)) {
            setTier(product.sizes[0].tier);
        }
    }, [product, tier]);

    // Map dimensions are still pulled from the FORMATS table — Prodigi's
    // SKU dictates the physical paper size, but we still need w/h on
    // the order doc for analytics & the generate_final_store call.
    const getSizeDimensions = (sizeTier) => {
        const raw = FORMATS[selectedFormat.value]?.[sizeTier];
        if (!raw) return null;
        const [width, height] = raw.split('x').map(d => parseFloat(d.replace(',', '.')));
        return { width, height };
    };

    const availableTiers = useMemo(() => {
        const out = {};
        if (!product) return out;
        for (const s of product.sizes) {
            const dim = getSizeDimensions(s.tier);
            const dpi = (maxDpis && maxDpis[s.tier]) || 0;
            out[s.tier] = !!dim && dpi >= PRINT_MIN_DPI;
        }
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product, maxDpis, selectedFormat]);

    const quote = quotes[tier];
    const itemPrice = quote ? quote.item_price : 0;
    const shipping = quote ? quote.shipping_price : 0;

    const voucherDiscountVal = voucher?.discount || 0;
    const voucherDiscountType = voucher?.discountType;
    // Print orders only accept fixed-amount vouchers. Percentage
    // vouchers (the cheap "100% off" coupons from the digital flow)
    // are silently ignored here so the preview total matches what
    // the backend will actually charge — a banner in the summary
    // explains why the voucher line isn't applying.
    const voucherRejected = voucherDiscountVal > 0 && voucherDiscountType !== 'amount';
    // Voucher applies to the whole charge (item + shipping), not just
    // the item. So a €12 voucher fully covers an €8 item + €4 shipping;
    // the math here matches what the backend ends up charging.
    const applyVoucherToTotal = (amount) => {
        if (!voucherDiscountVal || voucherDiscountType !== 'amount') return amount;
        return Math.max(0, amount - voucherDiscountVal / 100);
    };

    const grossTotal = itemPrice + shipping;
    const totalDisplay = applyVoucherToTotal(grossTotal);
    // "Free" = the voucher covers item + shipping down to (effectively)
    // zero — no Stripe needed. We also treat sub-€0.50 remainders as
    // free since Stripe rejects charges below its €0.50 minimum; the
    // cents we eat are cheaper than turning the customer away.
    const isFree = totalDisplay < 0.5 && grossTotal > 0;
    // How much of the voucher actually gets consumed by this order.
    // Used both in the cost-breakdown line and in the "X used / Y
    // returned" hint on the voucher row.
    const voucherUsedDisplay = !voucherRejected && voucherDiscountVal > 0
        ? Math.min(grossTotal, voucherDiscountVal / 100)
        : 0;
    const shippingComplete = isShippingComplete(freeShipping);

    const handlePurchase = async () => {
        if (!product) return;
        const dimensions = getSizeDimensions(tier);
        if (!dimensions) return;
        if (isPortrait) {
            [dimensions.width, dimensions.height] = [dimensions.height, dimensions.width];
        }

        let shippingPayload = null;
        if (isFree) {
            if (!shippingComplete) {
                setFreeShippingError('Please complete the shipping address.');
                setShippingDialogOpen(true);
                return;
            }
            shippingPayload = { ...freeShipping };
        }

        await onPrintSelect({
            productId: product.id,
            tier,
            color: product.kind === 'framed' ? color : null,
            dimensions,
            voucher: voucher?.code || null,
            shipping: shippingPayload,
            email: shippingPayload?.email,
        });
    };

    const handleContinueToSummary = () => {
        setFreeShippingError(null);
        setView('summary');
    };
    const handleBackToChoose = () => setView('choose');

    const sideBySide = isPortrait;

    if (catalogueErr) {
        return (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                Couldn't load the print catalogue: {catalogueErr}
            </div>
        );
    }
    if (!catalogue.length || !product) {
        return (
            <div className="p-6 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">Loading catalogue…</span>
            </div>
        );
    }

    const previewBlock = (
        <div className="py-1 sm:py-2">
            <FramedPreview imageUrl={finalImage} product={product} colorId={color}
                isPortrait={isPortrait} sideBySide={sideBySide}
                compact={view === 'summary'} />
            {/* Caption mirrors the catalogue subtitle so the paper / frame
                description stays in sync with whatever product the user
                has selected (no more hardcoded "Hahnemühle"). */}
            <p className="mt-1.5 sm:mt-3 text-[11px] sm:text-xs text-gray-500 text-center">
                {product.subtitle}
            </p>
        </div>
    );

    const choicesBlock = (
        <div className="space-y-2 sm:space-y-2.5">
            {/* Product + Size sit side-by-side on ≥sm screens with
                "1. Choose a Product" / "2. Choose the Size" labels
                above them. On mobile (and in portrait mode, where the
                right column is narrow because the image takes up the
                left half) the cards stack vertically and the step
                labels are hidden entirely. */}
            <div className={`flex flex-col gap-2 p-2 ${sideBySide ? '' : 'sm:flex-row sm:gap-3'}`}>
                <div className={`flex items-center gap-2 ${sideBySide ? '' : 'sm:flex-col sm:items-stretch sm:gap-1 sm:flex-1'}`}>
                    <div className={`text-sm text-center font-medium text-gray-700 px-1 ${sideBySide ? 'hidden' : 'hidden sm:block'}`}>
                        1. Choose a Product
                    </div>
                    <div className="flex-1 min-w-0 w-full">
                        <PickerButton
                            icon={Package}
                            title={product.label}
                            subtitle={product.subtitle}
                            // "from €X" — the cheapest tier's price for this product.
                            // Stable across size changes so the product row anchors
                            // the base price while the size row below shows the
                            // upcharge for picking a larger tier.
                            priceLabel={(() => {
                                if (!quote) return null;
                                const tierPrices = Object.values(quotes)
                                    .map(q => q?.item_price)
                                    .filter(v => typeof v === 'number');
                                const cheapest = tierPrices.length > 0 ? Math.min(...tierPrices) : quote.item_price;
                                return `from €${cheapest.toFixed(2)}`;
                            })()}
                            complete
                            onClick={() => setProductDialogOpen(true)}
                        />
                    </div>
                </div>

                <div className={`flex items-center gap-2 ${sideBySide ? '' : 'sm:flex-col sm:items-stretch sm:gap-1 sm:flex-1'}`}>
                    <div className={`text-sm text-center font-medium text-gray-700 px-1 ${sideBySide ? 'hidden' : 'hidden sm:block'}`}>
                        2. Choose the Size
                    </div>
                    <div className="flex-1 min-w-0 w-full">
                        <PickerButton
                            icon={Ruler}
                            title={`Size ${tier}`}
                            subtitle={(() => {
                                const s = product.sizes.find(x => x.tier === tier);
                                return s ? `${s.cm} cm` : '—';
                            })()}
                            // Size shows the upcharge vs the smallest tier rather
                            // than the full price (which would double up with the
                            // product row). `+€0.00` for the cheapest tier;
                            // `+€2.40` when picking a bigger size.
                            priceLabel={(() => {
                                if (!quote) return null;
                                const tierPrices = Object.values(quotes)
                                    .map(q => q?.item_price)
                                    .filter(v => typeof v === 'number');
                                if (tierPrices.length === 0) return `€${quote.item_price.toFixed(2)}`;
                                const cheapest = Math.min(...tierPrices);
                                const delta = quote.item_price - cheapest;
                                return delta > 0 ? `+€${delta.toFixed(2)}` : '+€0.00';
                            })()}
                            complete={!!availableTiers[tier]}
                            onClick={() => setSizeDialogOpen(true)}
                        />
                    </div>
                </div>
            </div>

            {product.kind === 'framed' && (product.colors || []).length > 0 && (
                <PickerButton
                    icon={null}
                    title={`Frame colour: ${color || '—'}`}
                    subtitle="Pick the frame finish"
                    complete={!!color}
                    onClick={() => setColorDialogOpen(true)}
                />
            )}
        </div>
    );

    const summaryBlock = (
        <div className="flex flex-col items-center pt-1">
            {quotesLoading || !quote ? (
                <div className="text-xs text-gray-500 flex items-center gap-1.5 mb-1 h-[18px]">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Calculating price…</span>
                </div>
            ) : (
                <>
                    <div className="text-xs text-gray-500">
                        Print €{itemPrice.toFixed(2)}
                        {shipping > 0 && ` + Shipping €${shipping.toFixed(2)}`}
                    </div>
                    {/* When a voucher reduces the price, show both:
                        the original total (strikethrough, muted) and
                        the new total (green). Otherwise just the
                        single bold total. */}
                    <div className="text-base sm:text-lg font-semibold mb-1">
                        {voucherUsedDisplay > 0 ? (
                            <>
                                <span className="line-through text-gray-400 mr-2 text-sm font-normal">
                                    €{grossTotal.toFixed(2)}
                                </span>
                                <span className="text-green-600">€{totalDisplay.toFixed(2)}</span>
                            </>
                        ) : (
                            <span className="text-gray-900">Total €{totalDisplay.toFixed(2)}</span>
                        )}
                    </div>
                </>
            )}
            <p className="text-[11px] text-gray-500 mb-2 text-center max-w-xs">
                Vouchers can be applied at the summary step.
            </p>
            <Button variant="black" onClick={handleContinueToSummary}
                disabled={!availableTiers[tier] || isUnlocking || quotesLoading || !quote}
                className="w-auto text-sm sm:text-base">
                {quotesLoading || !quote ? (
                    <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Calculating price…
                    </>
                ) : (
                    <>
                        <Check className="mr-2 h-4 w-4" />
                        Continue to summary
                    </>
                )}
            </Button>
            <p className="text-[10px] sm:text-[11px] text-gray-500 text-center mt-1.5 max-w-xs px-2">
                Review your order on the next step before placing it.
            </p>
        </div>
    );

    // Right column / bottom strip swaps between catalogue chooser
    // (choicesBlock + summaryBlock CTA) and the inline SummaryPanel.
    // The image stays put in either view.
    const activePanel = view === 'summary' ? (
        <SummaryPanel
            onBack={handleBackToChoose}
            onConfirm={async () => { await handlePurchase(); }}
            isUnlocking={isUnlocking}
            product={product}
            color={color}
            quote={quote}
            voucher={voucher}
            applyVoucher={applyVoucherToTotal}
            isFree={isFree}
            shippingComplete={shippingComplete}
            freeShipping={freeShipping}
            onOpenShippingDialog={() => setShippingDialogOpen(true)}
            tier={tier}
            voucherRejected={voucherRejected}
        />
    ) : (
        // CTA sits at the bottom of the choices column (mt-auto pushes
        // it down so it aligns with the image bottom on lg+ portrait
        // layouts where the grid items stretch to the same height).
        <div className="flex flex-col h-full">
            {choicesBlock}
            <div className="mt-auto pt-3">{summaryBlock}</div>
        </div>
    );

    return (
        <>
            <div className="space-y-2 sm:space-y-3">
                <div className={
                    sideBySide
                        ? 'flex flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-5 gap-2 lg:items-stretch'
                        : 'flex flex-col gap-2 sm:gap-3'
                }>
                    {previewBlock}
                    {activePanel}
                </div>
                {freeShippingError && (
                    <p className="text-xs text-red-500 text-center">{freeShippingError}</p>
                )}
            </div>

            <ProductDialog
                open={productDialogOpen}
                onClose={() => setProductDialogOpen(false)}
                productId={productId}
                onChange={setProductId}
                catalogue={catalogue}
                minPrices={minPrices}
            />

            <SizeDialog
                open={sizeDialogOpen}
                onClose={() => setSizeDialogOpen(false)}
                product={product}
                tier={tier}
                onChange={setTier}
                availableTiers={availableTiers}
                quotes={quotes}
            />

            <ColorDialog
                open={colorDialogOpen}
                onClose={() => setColorDialogOpen(false)}
                colors={product.colors}
                value={color}
                onChange={setColor}
            />

            <ShippingAddressDialog
                open={shippingDialogOpen}
                onClose={() => setShippingDialogOpen(false)}
                value={freeShipping}
                onChange={(next) => { setFreeShipping(next); setFreeShippingError(null); }}
            />

            {/* OrderSummaryDialog is replaced by the inline SummaryPanel
                above — kept as a comment so future readers don't go
                hunting for a dialog that no longer exists. */}
        </>
    );
};

export default MapPrintSection;
